import React from "react";
import { ButtonBase, TwoColumnSectionBase } from "@reusejs/react";
import { useRouter } from "next/router";
import { DateTime } from "luxon";
import { useSelector } from "react-redux";
import { CloudDownloadIcon } from "@heroicons/react/outline";

export interface AuctionEventCardProps {
  property: any;
  event: any;
}

export default function EventReportCard(props: AuctionEventCardProps) {
  const router = useRouter();

  return (
    <TwoColumnSectionBase
      firstColumnEl={<Content {...props} />}
      secondColumnEl={<Action {...props} />}
      twoColumnSectionBaseClasses={{
        firstElWrapperStyles: "w-full md:w-3/4 self-stretch",
        secondElWrapperStyles: "w-full md:w-1/4 border-l self-stretch p-4",
        columnsFlexWrapperStyles:
          "flex items-start justify-between border shadow rounded-lg flex-col md:flex-row bg-white",
      }}
    />
  );
}

const Content = (props: any) => {
  const getStatusItem = (status: string) => {
    let element;
    switch (status) {
      case "pending":
        element = (
          <div className="text-left px-4 py-2 bg-gray-100 rounded-bl-lg flex justify-between items-center">
            <div className="flex">
              <div className="text-xs text-gray-500">
                Update:{" "}
                <span className="font-semibold">
                  Auction event has been created and waiting for approval.{" "}
                </span>
              </div>
            </div>
          </div>
        );
        break;

      case "accepted":
        element = (
          <div className="text-left px-4 py-2 bg-green-100 rounded-bl-lg flex justify-between items-center">
            <div className="flex">
              <div className="text-xs text-green-500">
                Status:{" "}
                <span className="font-semibold">
                  Auction event has been accepted by the approver.
                </span>
              </div>
            </div>
          </div>
        );
        break;

      case "rejected":
        element = (
          <div className="text-left px-4 py-2 bg-red-100 rounded-bl-lg flex justify-between items-center">
            <div className="flex">
              <div className="text-xs text-red-500">
                Status:{" "}
                <span className="font-semibold">
                  Auction event has been rejected by the approver.
                </span>
              </div>
            </div>
          </div>
        );
        break;

      case "live":
        element = (
          <div className="text-left px-4 py-2 bg-blue-100 rounded-bl-lg flex justify-between items-center">
            <div className="flex">
              <div className="text-xs text-blue-500">
                Status:{" "}
                <span className="font-semibold">Auction event is live.</span>
              </div>
            </div>
          </div>
        );
        break;

      case "end":
        element = (
          <div className="text-left px-4 py-2 bg-red-100 rounded-bl-lg flex justify-between items-center">
            <div className="flex">
              <div className="text-xs text-red-500">
                Status:{" "}
                <span className="font-semibold">Auction event has ended.</span>
              </div>
            </div>
          </div>
        );
        break;

      default:
        break;
    }
    return element;
  };
  return (
    <div className="flex-1">
      <div className="text-left px-4 py-2 border-b text-xs text-gray-500">
        Event ID: {props.event.listing_id}
      </div>
      <div className="text-left border-b px-4 py-4 flex justify-between h-20">
        <div className="flex flex-col">
          <div className="text-xs text-gray-500">Event Name</div>
          <div className="text-sm break-all">{props.event.event_name}</div>
        </div>
      </div>
      <div className="text-left border-b px-4 flex justify-between">
        <div className="flex basis-1/2 border-r py-4 flex-col">
          <div className="text-xs text-gray-500">
            Bidding Start Date and Time
          </div>
          <div className="text-sm pt-1">
            <div className="flex items-center divide-x divide-gray-400">
              <div className="pr-3">
                {props.event.start_date_time &&
                  DateTime.fromISO(props.event.start_date_time)
                    .setZone("Asia/Kolkata")
                    .toFormat("dd MMMM yyyy")}
              </div>
              <div className="px-3 text-xs">
                {props.event.start_date_time &&
                  `${DateTime.fromISO(props.event.start_date_time)
                    .setZone("Asia/Kolkata")
                    .toFormat("hh:mm a")} (IST)`}
              </div>
            </div>
          </div>
        </div>
        <div className="flex basis-1/2 py-4 flex-col pl-6">
          <div className="text-xs text-gray-500">Bidding End Date and Time</div>
          <div className="text-sm pt-1">
            <div className="flex items-center divide-x divide-gray-400">
              <div className="pr-3">
                {props.property.auction_end_date_time &&
                  DateTime.fromISO(props.property.auction_end_date_time)
                    .setZone("Asia/Kolkata")
                    .toFormat("dd MMMM yyyy")}
              </div>
              <div className="px-3 text-xs">
                {props.property.auction_end_date_time &&
                  `${DateTime.fromISO(props.property.auction_end_date_time)
                    .setZone("Asia/Kolkata")
                    .toFormat("hh:mm a")} (IST)`}
              </div>
            </div>
          </div>
        </div>
      </div>
      {getStatusItem(props.event.status)}
    </div>
  );
};

const Action = (props: any) => {
  const router = useRouter();

  const getStatusItem = (status: string) => {
    let element;

    switch (status) {
      case "pending":
        element = (
          <div className="text-2xl font-bold text-gray-500">Pending</div>
        );
        break;

      case "accepted":
        element = (
          <div className="text-2xl font-bold text-green-500">Accepted</div>
        );
        break;

      case "rejected":
        element = (
          <div className="text-2xl font-bold text-red-500">Rejected</div>
        );
        break;

      case "live":
        element = <div className="text-2xl font-bold text-blue-500">Live</div>;
        break;

      case "end":
        element = <div className="text-2xl font-bold text-red-500">Ended</div>;
        break;

      default:
        break;
    }
    return element;
  };

  return (
    <div className="flex flex-col h-full justify-between">
      <div>
        <div className="text-left flex justify-center">
          <div className="flex basis-1/2 flex-col items-center">
            <div className="text-base text-gray-500">Status</div>
            {getStatusItem(props.event.status)}
          </div>
        </div>
      </div>
      <div className="space-y-4">
        <ButtonBase
          variant="primary"
          type="submit"
          label={"Download Report"}
          onClick={() => {
            if (props.event?.pdfs.length > 0) {
              // router.push(
              //   `${process.env.NEXT_PUBLIC_IMAGIX_URL}/${props.event.pdfs[0]?.key_path}?dl`
              // );ss
              router.push(props.event.pdfs[0]?.download_url);
            } else {
              console.log("PDF not found..!");
            }
          }}
          buttonPrefix={<CloudDownloadIcon className="h-6 w-6 mr-3" />}
          buttonBaseClasses={{
            font: "text-base font-medium",
            backgroundColor: "bg-green-600 hover:bg-green-700",
            textColor: "text-white",
            border: "any",
            width: "w-full",
            padding: "px-4 py-3",
          }}
        />
        <ButtonBase
          variant="primary"
          type="submit"
          label={"View Report"}
          onClick={() =>
            router.push(`/dashboard/reports/${props.event?.listing_id}`)
          }
          buttonBaseClasses={{
            font: "text-base font-medium",
            backgroundColor: "bg-blue-600 hover:bg-blue-700",
            textColor: "text-white",
            border: "any",
            width: "w-full",
            padding: "px-4 py-3",
          }}
        />
      </div>
    </div>
  );
};
